import React, { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Collapse,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { makeGetRequest } from 'utils/api'
import urls from 'utils/urls'

/**
 * TimeRecommandationsHistoryModal — Historique des recommandations de gestion
 * du temps générées par le Life Agent. Miroir strict de la version
 * patrimoniale (MonPatrimoine/Component/Card/RecommandationsHistoryModal).
 *
 * LAP-765-CARD — Endpoint backend (réel) :
 *   GET /api/general/synthese-temps-reports/?history=true
 *     → array d'objets { id, content, source, created_at, … } (DisplayedAdvice
 *       module "Temps") triés du plus récent au plus ancien.
 *
 * Sur une réponse valide on affiche le vrai historique (liste vide => état
 * vide réel). buildMockHistory n'est plus qu'un filet de sécurité déclenché
 * si l'endpoint est injoignable (backend pas encore déployé).
 */
function TimeRecommandationsHistoryModal({ open, onClose, currentReport }) {
  const { t, i18n } = useTranslation('layout')
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(null)

  const lang = (i18n.language || 'fr').split('-')[0]

  useEffect(() => {
    if (!open) return

    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await makeGetRequest(
          `${urls.getSynthesTemps}?history=true`,
          {},
          true,
        )
        const list = Array.isArray(data)
          ? data
          : data?.history || data?.data || data?.conseils_history || []

        const normalised = list
          .map(normaliseEntry)
          .filter((e) => e.content && String(e.content).trim().length > 0)

        // LAP-765-CARD — L'endpoint `?history=true` renvoie désormais le vrai
        // historique (DisplayedAdvice module "Temps"). Sur une réponse valide,
        // on lui fait confiance : une liste vide affiche le véritable état vide
        // (pas de mock). Le repli mock ne sert plus qu'au cas où l'endpoint
        // est carrément injoignable (catch ci-dessous), p.ex. backend pas
        // encore déployé.
        const hasCurrent = currentReport?.content
          ? normalised.some((e) => e.content === currentReport.content)
          : false
        const finalList =
          hasCurrent || !currentReport?.content
            ? normalised
            : [normaliseEntry(currentReport), ...normalised]
        setHistory(finalList)
      } catch (e) {
        // LAP-765-CARD — Repli mock uniquement si l'endpoint history est
        // injoignable (réseau / backend pas encore déployé).
        console.warn(
          '[TimeConseilsCard] /history=true endpoint pas dispo, fallback mock',
          e?.message,
        )
        setHistory(buildMockHistory(currentReport, lang))
        setError('mock')
      } finally {
        setLoading(false)
      }
    }
    fetchHistory()
  }, [open, currentReport, lang])

  const formatDate = (d) => {
    if (!d) return ''
    try {
      const dj = dayjs(d).locale(lang === 'fr' ? 'fr' : 'en')
      return dj.format(lang === 'fr' ? 'D MMMM YYYY · HH[h]mm' : 'MMMM D, YYYY · HH:mm')
    } catch {
      return String(d)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#0e1a36',
          border: '1px solid rgba(155,140,255,0.25)',
          borderRadius: 3,
          color: 'white',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          py: 2,
        }}
      >
        <HistoryIcon sx={{ color: '#9b8cff' }} />
        <Box flex={1}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t('syntheseTemps.historyTitle', {
              defaultValue: 'Historique des recommandations',
            })}
          </Typography>
          <Typography variant="caption" sx={{ color: '#a0b4d6' }}>
            {t('syntheseTemps.historySubtitle', {
              defaultValue:
                'Toutes les recommandations temps générées par votre Life Agent',
            })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#a0b4d6' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, maxHeight: 520 }}>
        {loading ? (
          <Box
            sx={{
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: '#9b8cff' }} />
            <Typography variant="body2" sx={{ color: '#a0b4d6' }}>
              {t('syntheseTemps.historyLoading', {
                defaultValue: 'Chargement de l\'historique…',
              })}
            </Typography>
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#a0b4d6' }}>
              {t('syntheseTemps.historyEmpty', {
                defaultValue: 'Aucune recommandation dans l\'historique pour le moment.',
              })}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {error === 'mock' && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 1,
                  bgcolor: 'rgba(251,191,36,0.10)',
                  border: '1px solid rgba(251,191,36,0.3)',
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="caption" sx={{ color: '#fbbf24' }}>
                  {t('syntheseTemps.historyMockNotice', {
                    defaultValue:
                      'Données de démonstration · l\'endpoint historique backend sera bientôt disponible',
                  })}
                </Typography>
              </Box>
            )}
            {history.map((entry, idx) => {
              const isOpen = expanded === entry.id
              const isCurrent = idx === 0
              return (
                <Box
                  key={entry.id || idx}
                  sx={{
                    bgcolor: isCurrent
                      ? 'rgba(155,140,255,0.10)'
                      : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${
                      isCurrent ? 'rgba(155,140,255,0.30)' : 'rgba(255,255,255,0.06)'
                    }`,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    onClick={() => setExpanded(isOpen ? null : entry.id)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: '#9b8cff', fontSize: 18 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#dbe5f7',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previewContent(entry.content)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="caption" sx={{ color: '#a0b4d6' }}>
                          {formatDate(entry.date)}
                        </Typography>
                        {isCurrent && (
                          <Chip
                            label={t('syntheseTemps.historyCurrentBadge', {
                              defaultValue: 'En cours',
                            })}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(155,140,255,0.25)',
                              color: '#d4ccff',
                              fontWeight: 600,
                              height: 18,
                              fontSize: 10,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {isOpen ? (
                      <ExpandLessIcon sx={{ color: '#a0b4d6' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#a0b4d6' }} />
                    )}
                  </Box>
                  <Collapse in={isOpen}>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        bgcolor: 'rgba(0,0,0,0.15)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#dbe5f7',
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {entry.content}
                      </Typography>
                      {entry.source && (
                        <Box
                          mt={1.5}
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          justifyContent="flex-end"
                        >
                          <AutoAwesomeIcon sx={{ fontSize: 14, color: '#9b8cff' }} />
                          <Typography variant="caption" sx={{ color: '#9b8cff' }}>
                            {entry.source}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

function previewContent(text) {
  if (!text) return ''
  const oneLine = String(text).replace(/\s+/g, ' ').trim()
  return oneLine.length > 90 ? oneLine.slice(0, 90) + '…' : oneLine
}

function normaliseEntry(raw) {
  if (!raw)
    return { id: Math.random().toString(36), content: '', source: 'Life Agent', date: null }

  const data = raw.data || raw.advice || raw.displayed_advice || raw.payload || raw

  return {
    id:
      data.id ||
      raw.id ||
      data.uuid ||
      raw.uuid ||
      data.created_at ||
      raw.created_at ||
      Math.random().toString(36),
    content:
      data.content ||
      data.text ||
      data.advice ||
      data.message ||
      data.recommendation ||
      raw.content ||
      raw.text ||
      '',
    source: data.source || raw.source || 'Life Agent',
    date:
      data.created_at ||
      data.createdAt ||
      data.date ||
      data.generated_at ||
      data.timestamp ||
      raw.created_at ||
      raw.createdAt ||
      raw.date,
  }
}

// LAP-765-CARD — TODO BACKEND : à supprimer une fois l'endpoint history dispo
function buildMockHistory(currentReport, lang) {
  const now = new Date()
  const daysAgo = (n) => new Date(now.getTime() - n * 24 * 3600 * 1000).toISOString()
  const items = []
  if (currentReport) {
    items.push(
      normaliseEntry({
        ...currentReport,
        id: 'current',
        date: currentReport.date || currentReport.created_at || now.toISOString(),
      }),
    )
  }
  const samples = {
    fr: [
      'Bloquez chaque matin un créneau « deep work » de 2h sans notifications pour avancer sur votre projet principal.',
      'Regroupez vos tâches administratives en une seule session hebdomadaire au lieu de les disperser dans la semaine.',
      'Planifiez 3 séances de sport de 45 min : la régularité prime sur l\'intensité pour tenir dans la durée.',
      'Terminez chaque vendredi par une revue de semaine de 30 min : bilan, replanification, célébration des wins.',
    ],
    en: [
      'Block a 2h “deep work” slot each morning with no notifications to move your main project forward.',
      'Batch your admin tasks into a single weekly session instead of scattering them across the week.',
      'Schedule 3 × 45 min workouts: consistency beats intensity for staying on track over time.',
      'End each Friday with a 30 min weekly review: recap, replan, celebrate the wins.',
    ],
  }
  const list = samples[lang] || samples.fr
  list.forEach((content, idx) => {
    items.push(
      normaliseEntry({
        id: `mock-${idx}`,
        content,
        source: 'Life Agent',
        date: daysAgo((idx + 1) * 7),
      }),
    )
  })
  return items
}

export default TimeRecommandationsHistoryModal
